// file-extract.ts — Phase: turn attached plan/text files into a structured list
// of items in ONE deterministic AI call (responseSchema JSON), instead of the
// less-reliable multi-turn tool loop. Output feeds normalizeBatchItems →
// create_items proposal → applyBatchToState (all existing).

import type { AiGenerateRequest } from "./ai-client";
import { formatFilesForPrompt } from "./read-text-files";
import type { AttachedFile } from "./read-text-files";

// JSON Schema for the plan. Gemini-safe: type/properties/items/enum/required only
// (no additionalProperties / minimum / maximum).
export const FILE_PLAN_SCHEMA: Record<string, unknown> = {
  type: "object",
  required: ["items"],
  properties: {
    items: {
      type: "array",
      items: {
        type: "object",
        required: ["kind", "title"],
        properties: {
          kind: { type: "string", enum: ["task", "project", "goal", "habit", "note"] },
          title: { type: "string" },
          description: { type: "string" },
          priority: { type: "string", enum: ["low", "medium", "high"] },
          dueDate: { type: "string" },
          estimateMinutes: { type: "number" },
          subtasks: { type: "array", items: { type: "string" } },
          tags: { type: "array", items: { type: "string" } },
          projectName: { type: "string" },
          goalName: { type: "string" },
          area: { type: "string" },
          horizon: { type: "string", enum: ["quarter", "year", "life"] },
          metric: { type: "string" },
          unit: { type: "string" },
          target: { type: "number" },
          targetPerWeek: { type: "number" },
          category: { type: "string", enum: ["project", "area", "resource", "archive"] },
        },
      },
    },
  },
};

/** Shape returned by generateJson with FILE_PLAN_SCHEMA. */
export interface FilePlanResult {
  items?: unknown[];
}

/**
 * Build the extraction request. `userNote` is the user's optional typed message
 * alongside the files (extra instruction). `todayIso` resolves relative dates.
 */
export function buildFileExtractRequest(
  files: AttachedFile[],
  todayIso: string,
  userNote = "",
): AiGenerateRequest {
  const prompt = `Hôm nay là ${todayIso}. Bạn là trợ lý lập kế hoạch của Personal Life OS.
Đọc kỹ (các) tệp đính kèm và TRÍCH thành danh sách mục để tạo trong app, trả về JSON đúng schema.

Quy tắc bóc tách:
- Mỗi "phase"/giai đoạn/dự án lớn → kind="project". Mục tiêu tổng/định hướng dài hạn → kind="goal".
- Mỗi đầu việc / checkbox / "todo" → kind="task", gắn vào dự án bằng projectName (đặt ĐÚNG tên project bạn vừa tạo) và goalName nếu hợp lý.
- Các bước con của một việc → đưa vào subtasks (mảng chuỗi).
- Thói quen lặp lại theo lịch (vd "3 buổi/tuần") → kind="habit" với targetPerWeek.
- Tài liệu/ghi chú tham khảo → kind="note" (category PARA nếu rõ).
- Liên kết bằng TÊN: đặt projectName/goalName trùng đúng title của project/goal trong cùng danh sách để app tự nối.
- Ngày tương đối ("tháng sau", "cuối năm"...) → quy đổi YYYY-MM-DD theo hôm nay ${todayIso}. Không chắc thì bỏ trống.
- Giữ nguyên tiếng Việt. KHÔNG bịa mục không có trong tệp.
${userNote ? `\nGhi chú thêm của người dùng: "${userNote}"\n` : ""}
${formatFilesForPrompt(files)}

Chỉ trả về JSON hợp lệ theo schema (một object có khoá "items").`;

  return {
    contents: [{ role: "user", parts: [{ text: prompt }] }],
    generationConfig: { maxOutputTokens: 8192, temperature: 0.2 },
    responseSchema: FILE_PLAN_SCHEMA,
  };
}
